import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { translations } from "@/i18n/translations";

export type LanguageCode = "EN" | "DE" | "FR" | "IT" | "ES" | "AR" | "ZH-TW";

export const LANGUAGES: { code: LanguageCode; label: string }[] = [
  { code: "EN", label: "EN" },
  { code: "DE", label: "DE" },
  { code: "FR", label: "FR" },
  { code: "IT", label: "IT" },
  { code: "ES", label: "ES" },
  { code: "AR", label: "AR" },
  { code: "ZH-TW", label: "ZH-TW" },
];

const LANG_MAP: Record<LanguageCode, string> = {
  EN: "en",
  DE: "de",
  FR: "fr",
  IT: "it",
  ES: "es",
  AR: "ar",
  "ZH-TW": "zh-TW",
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem("motai-language");
    return (saved as LanguageCode) || "EN";
  });

  const setLanguage = useCallback((lang: LanguageCode) => {
    setLanguageState(lang);
    localStorage.setItem("motai-language", lang);
    // Set dir attribute for RTL support
    document.documentElement.dir = lang === "AR" ? "rtl" : "ltr";
  }, []);

  const t = useCallback(
    (key: string): string => {
      const langKey = LANG_MAP[language];
      return translations[langKey]?.[key] || translations["en"]?.[key] || key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
